"use client"

import { useCallback, useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Save, Key, ShieldOff, Eye, EyeOff, Loader2, CheckCircle, XCircle, Phone, Plus, ChevronDown, Bot, ArrowRight, ShoppingBag, Instagram, MessageCircle, Music2 } from "lucide-react"
import { useSystemSettings } from "@/hooks/use-system-settings"
import { useAuth } from "@/hooks/use-auth"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { LogoSettingsSection } from "./logo-settings-section"
import { ElevenLabsAccountDialog, type ElevenLabsAccountData } from "@/components/billing/elevenlabs-account-dialog"
import { DEFAULT_SCOPES as SHOPIFY_DEFAULT_SCOPES, SHOPIFY_APP_SETTING_KEYS } from "@/lib/integrations/oauth/shopify"
import { parseShopDomainList } from "@/lib/shopify/shop-domain"
import { maskSecret } from "@/lib/mask-secret"
import { useUpdateTiktokWebhookSubscriptionMutation } from "@/hooks/mutations/tiktok-webhook-subscription"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





/**
 * A pool row as the admin endpoint returns it: the dialog's shape plus the
 * fields only the list needs. Extending it (rather than restating it) keeps the
 * row assignable to the edit dialog — it used to drop `webhook_name`.
 */
interface PoolAccount extends ElevenLabsAccountData {
  id: string
  status: string
}

export function SystemSettingsPage() {


  const tUi = useTranslations("SystemSettings.SystemSettingsSystemSettingsPage")

  const router = useRouter()
  const { user, isLoading: authLoading, fetchWithAuth, isAuthenticated } = useAuth()
  const { settings, isLoading, updateSetting, getSetting } = useSystemSettings()

  const [googleClientId, setGoogleClientId] = useState("")
  const [googlePickerApiKey, setGooglePickerApiKey] = useState("")
  const [googleClientSecret, setGoogleClientSecret] = useState("")
  const [showGoogleSecret, setShowGoogleSecret] = useState(false)

  // Shopify OAuth state — the single first-party PUBLIC app (App-Pricing billing +
  // catalog/attribution). `shopify_app_handle` builds the hosted plan-selection URL.
  const [shopifyClientId, setShopifyClientId] = useState("")
  const [shopifyClientSecret, setShopifyClientSecret] = useState("")
  const [shopifyScopes, setShopifyScopes] = useState("")
  const [shopifyHandle, setShopifyHandle] = useState("")
  const [shopifySignInEnabled, setShopifySignInEnabled] = useState(false)
  const [shopifyTestShops, setShopifyTestShops] = useState("")
  const [showShopifySecret, setShowShopifySecret] = useState(false)
  const [shopifySecretConfigured, setShopifySecretConfigured] = useState(false)
  const [shopifySecretHint, setShopifySecretHint] = useState("")

  // Shared Meta App OAuth state
  const [metaAppId, setMetaAppId] = useState("")
  const [metaAppSecret, setMetaAppSecret] = useState("")
  const [metaWebhookVerifyToken, setMetaWebhookVerifyToken] = useState("")
  const [metaSsoConfigId, setMetaSsoConfigId] = useState("")
  const [metaFbConfigId, setMetaFbConfigId] = useState("")
  const [metaLeadAdsConfigId, setMetaLeadAdsConfigId] = useState("")
  const [metaWhatsappConfigId, setMetaWhatsappConfigId] = useState("")
  const [showMetaSecret, setShowMetaSecret] = useState(false)
  const [metaSecretConfigured, setMetaSecretConfigured] = useState(false)

  // Instagram OAuth state (separate -IG app from Meta)
  const [instagramAppId, setInstagramAppId] = useState("")
  const [instagramAppSecret, setInstagramAppSecret] = useState("")
  const [showInstagramSecret, setShowInstagramSecret] = useState(false)
  const [instagramSecretConfigured, setInstagramSecretConfigured] = useState(false)

  // TikTok Business Messaging state (business-api.tiktok.com developer app)
  const [tiktokAppId, setTiktokAppId] = useState("")
  const [tiktokAppSecret, setTiktokAppSecret] = useState("")
  const [tiktokAuthorizationUrl, setTiktokAuthorizationUrl] = useState("")
  const [showTiktokSecret, setShowTiktokSecret] = useState(false)
  const [tiktokSecretConfigured, setTiktokSecretConfigured] = useState(false)
  const updateTiktokWebhook = useUpdateTiktokWebhookSubscriptionMutation()

  // ElevenLabs Pool accounts state
  const [poolAccounts, setPoolAccounts] = useState<PoolAccount[]>([])
  const [isLoadingPool, setIsLoadingPool] = useState(true)
  const [elDialogOpen, setElDialogOpen] = useState(false)
  const [elDialogAccount, setElDialogAccount] = useState<ElevenLabsAccountData | null>(null)

  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle")
  const [statusMessage, setStatusMessage] = useState("")

  const isSuperUser = user?.role === "super_user"
  const shopifyTestShopList = parseShopDomainList(shopifyTestShops)

  const loadPoolAccounts = useCallback(async () => {
    setIsLoadingPool(true)
    try {
      const response = await fetchWithAuth("/api/admin/billing/elevenlabs/accounts")
      if (response.ok) {
        const data = await response.json()
        setPoolAccounts(data.accounts || [])
      }
    } catch (error) {
      console.error("Failed to load pool accounts:", error)
    } finally {
      setIsLoadingPool(false)
    }
  }, [fetchWithAuth])

  // Load ElevenLabs pool accounts
  useEffect(() => {
    if (isSuperUser && isAuthenticated) {
      void loadPoolAccounts()
    }
  }, [isSuperUser, isAuthenticated, loadPoolAccounts])

  useEffect(() => {
    if (!isLoading && settings.length > 0) {
      const cid = getSetting("google_client_id")
      const csec = getSetting("google_client_secret")
      setGoogleClientId(cid?.value || "")
      setGoogleClientSecret(csec?.value || "")
      setGooglePickerApiKey(getSetting("google_picker_api_key")?.value || "")

      // Shopify settings — the single first-party public app
      const sId = getSetting(SHOPIFY_APP_SETTING_KEYS.clientId)
      const sSec = getSetting(SHOPIFY_APP_SETTING_KEYS.clientSecret)
      const sScopes = getSetting(SHOPIFY_APP_SETTING_KEYS.scopes)
      const sHandle = getSetting(SHOPIFY_APP_SETTING_KEYS.handle)
      setShopifyClientId(sId?.value || "")
      setShopifySecretConfigured(!!sSec?.value)
      setShopifySecretHint(sSec?.value || "")
      setShopifyClientSecret("") // never pre-fill — encrypted on server
      setShopifyScopes(sScopes?.value || SHOPIFY_DEFAULT_SCOPES)
      setShopifyHandle(sHandle?.value || "")
      setShopifySignInEnabled(getSetting(SHOPIFY_APP_SETTING_KEYS.signInEnabled)?.value === "true")
      setShopifyTestShops(getSetting(SHOPIFY_APP_SETTING_KEYS.testShops)?.value || "")

      // Meta (Facebook) settings
      const mId = getSetting("meta_app_id")
      const mSec = getSetting("meta_app_secret")
      const mWebhook = getSetting("meta_webhook_verify_token")
      const mSsoConfig = getSetting("meta_sso_config_id")
      const mFbConfig = getSetting("meta_app_config_id")
      const mLeadAdsConfig = getSetting("meta_lead_ads_config_id")
      const mWaConfig = getSetting("meta_whatsapp_config_id")
      setMetaAppId(mId?.value || "")
      setMetaSecretConfigured(!!mSec?.value)
      setMetaAppSecret("") // never pre-fill — encrypted on server
      setMetaWebhookVerifyToken(mWebhook?.value || "")
      setMetaSsoConfigId(mSsoConfig?.value || "")
      setMetaFbConfigId(mFbConfig?.value || "")
      setMetaLeadAdsConfigId(mLeadAdsConfig?.value || "")
      setMetaWhatsappConfigId(mWaConfig?.value || "")

      // Instagram settings (separate -IG app)
      const igId = getSetting("instagram_app_id")
      const igSec = getSetting("instagram_app_secret")
      setInstagramAppId(igId?.value || "")
      setInstagramSecretConfigured(!!igSec?.value)
      setInstagramAppSecret("") // never pre-fill — encrypted on server

      // TikTok settings
      const ttId = getSetting("tiktok_app_id")
      const ttSec = getSetting("tiktok_app_secret")
      const ttAuthUrl = getSetting("tiktok_authorization_url")
      setTiktokAppId(ttId?.value || "")
      setTiktokSecretConfigured(!!ttSec?.value)
      setTiktokAppSecret("") // never pre-fill — encrypted on server
      setTiktokAuthorizationUrl(ttAuthUrl?.value || "")
    }
  }, [isLoading, settings, getSetting])



  if (authLoading || isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-gray-500"><span key={tUi("loadingSystemSettings")}>{tUi("loadingSystemSettings")}</span></div>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2"><span key={tUi("authenticationRequired")}>{tUi("authenticationRequired")}</span></h3>
          <p className="text-gray-600"><span key={tUi("pleaseLogInToAccessSystemSettings")}>{tUi("pleaseLogInToAccessSystemSettings")}</span></p>
        </div>
      </div>
    )
  }

  if (!isSuperUser) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2"><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></h3>
          <p className="text-gray-600"><span key={tUi("onlySuperUsersCanAccessSystemSettings")}>{tUi("onlySuperUsersCanAccessSystemSettings")}</span></p>
          <p className="text-sm text-gray-500 mt-2"><span><span key={tUi("currentRole")}>{tUi("currentRole")}</span></span> {user.role}</p>
        </div>
      </div>
    )
  }

  const handleSave = async (saves: Array<() => Promise<unknown>>) => {
    setSaveStatus("saving")
    try {
      for (const save of saves) {
        await save()
      }
      setSaveStatus("success")
      setStatusMessage("Settings saved successfully!")
      setTimeout(() => {
        setSaveStatus("idle")
        setStatusMessage("")
      }, 3000)
    } catch (error: unknown) {
      setSaveStatus("error")
      setStatusMessage(error instanceof Error ? error.message : "Failed to save settings")
      setTimeout(() => {
        setSaveStatus("idle")
        setStatusMessage("")
      }, 3000)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground"><span key={tUi("systemSettings")}>{tUi("systemSettings")}</span></h1>
          <p className="text-gray-600 mt-1"><span key={tUi("configureSystemWideSettingsAndAPIIntegrations")}>{tUi("configureSystemWideSettingsAndAPIIntegrations")}</span></p>
        </div>
      </div>

      {/* AI Settings Quick Access */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
        <CardContent className="flex items-center justify-between p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground"><span key={tUi("aIConfiguration")}>{tUi("aIConfiguration")}</span></h3>
              <p className="text-sm text-muted-foreground">
                <span key={tUi("manageAIProvidersModelsAndUseCaseConfigurations")}>{tUi("manageAIProvidersModelsAndUseCaseConfigurations")}</span></p>
            </div>
          </div>
          <Button
            onClick={() => router.push("?page=ai-settings")}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <span><span key={tUi("openAISettings")}>{tUi("openAISettings")}</span></span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {statusMessage && (
        <Alert className={saveStatus === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
          <AlertDescription className={saveStatus === "success" ? "text-green-800" : "text-red-800"}>
            {statusMessage}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <LogoSettingsSection />

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    <span><span key={"Google OAuth"}>{"Google OAuth"}</span></span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="google-client-id"><span key={tUi("googleClientID")}>{tUi("googleClientID")}</span></Label>
                  <Input
                    id="google-client-id"
                    type="text"
                    placeholder={tUi("yourGoogleOAuthClientID")}
                    value={googleClientId}
                    onChange={(e) => setGoogleClientId(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="google-client-secret"><span key={tUi("googleClientSecret")}>{tUi("googleClientSecret")}</span></Label>
                  <div className="relative">
                    <Input
                      id="google-client-secret"
                      type={showGoogleSecret ? "text" : "password"}
                      placeholder={tUi("yourGoogleOAuthClientSecret")}
                      value={googleClientSecret}
                      onChange={(e) => setGoogleClientSecret(e.target.value)}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowGoogleSecret((v) => !v)}
                    >
                      {showGoogleSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {googleClientSecret && !showGoogleSecret && (
                    <div className="text-xs text-gray-500"><span><span key={tUi("currentSecret")}>{tUi("currentSecret")}</span></span> {maskSecret(googleClientSecret)}</div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="google-picker-api-key"><span>{tUi("googlePickerApiKey")}</span></Label>
                  <Input
                    id="google-picker-api-key"
                    type="text"
                    value={googlePickerApiKey}
                    onChange={event => setGooglePickerApiKey(event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground"><span>{tUi("googlePickerApiKeyHint")}</span></p>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() =>
                      handleSave([
                        () => updateSetting("google_client_id", googleClientId),
                        () => updateSetting("google_picker_api_key", googlePickerApiKey),
                        () => updateSetting("google_client_secret", googleClientSecret),
                      ])
                    }
                    disabled={saveStatus === "saving"}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      tUi("saving")
                    ) : (
                      <><TranslationText>{tUi.rich("tag0Tag0Save", { tag0: () => (<Save className="w-4 h-4 mr-2" />) })}</TranslationText></>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500">
                  <span><span key={tUi("googleAccountSSORedirectURI")}>{tUi("googleAccountSSORedirectURI")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/identity/callback/google"}>{"/api/identity/callback/google"}</span></span></code>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5" />
                    <span><span key={"Shopify OAuth"}>{"Shopify OAuth"}</span></span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="shopify-client-id"><span key={tUi("shopifyAppClientID")}>{tUi("shopifyAppClientID")}</span></Label>
                  <Input
                    id="shopify-client-id"
                    type="text"
                    placeholder={tUi("yourShopifyAppClientIDFromDevDashboard")}
                    value={shopifyClientId}
                    onChange={(e) => setShopifyClientId(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shopify-client-secret">
                    <span key={shopifySecretConfigured ? tUi("updateClientSecret") : tUi("shopifyAppClientSecret")}>{shopifySecretConfigured ? tUi("updateClientSecret") : tUi("shopifyAppClientSecret")}</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="shopify-client-secret"
                      type={showShopifySecret ? "text" : "password"}
                      placeholder={shopifySecretConfigured ? tUi("enterNewSecretToUpdate") : tUi("yourShopifyAppClientSecret")}
                      value={shopifyClientSecret}
                      onChange={(e) => setShopifyClientSecret(e.target.value)}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowShopifySecret((v) => !v)}
                    >
                      {showShopifySecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {shopifySecretConfigured && !shopifyClientSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      <span><span key={tUi("savedEncrypted")}>{tUi("savedEncrypted")}</span></span> <code className="bg-gray-100 px-1 rounded text-gray-600">{shopifySecretHint}</code>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shopify-scopes"><span key={tUi("oAuthScopes")}>{tUi("oAuthScopes")}</span></Label>
                  <Input
                    id="shopify-scopes"
                    type="text"
                    placeholder={"read_orders,read_customers,..."}
                    value={shopifyScopes}
                    onChange={(e) => setShopifyScopes(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    <span><span key={tUi("commaSeparatedAdd")}>{tUi("commaSeparatedAdd")}</span></span> <code className="bg-gray-100 px-1 rounded">read_all_orders</code> <span><span key={tUi("afterShopifyApprovalForFullOrderHistoryAccess")}>{tUi("afterShopifyApprovalForFullOrderHistoryAccess")}</span></span>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shopify-handle"><span key={tUi("appHandle")}>{tUi("appHandle")}</span></Label>
                  <Input
                    id="shopify-handle"
                    type="text"
                    placeholder={"your-app-handle"}
                    value={shopifyHandle}
                    onChange={(e) => setShopifyHandle(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    <span><span key={tUi("theAppSShopifyHandleThe")}>{tUi("theAppSShopifyHandleThe")}</span></span> <code className="bg-gray-100 px-1 rounded">&lt;handle&gt;</code> <span><span key={tUi("inTheHostedPlanURL")}>{tUi("inTheHostedPlanURL")}</span></span> <code className="bg-gray-100 px-1 rounded">.../charges/&lt;handle&gt;/pricing_plans</code><span><span key={tUi("mustMatchExactly")}>{tUi("mustMatchExactly")}</span></span>
                  </p>
                </div>

                <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                  <div className="min-w-0 space-y-1">
                    <Label htmlFor="shopify-sign-in-enabled"><span key={tUi("shopifySignIn")}>{tUi("shopifySignIn")}</span></Label>
                    <p className="text-xs text-gray-500">
                      <span key={tUi("storeOwnersOpeningTheAppFromTheirShopify")}>{tUi("storeOwnersOpeningTheAppFromTheirShopify")}</span></p>
                  </div>
                  <Switch
                    id="shopify-sign-in-enabled"
                    checked={shopifySignInEnabled}
                    onCheckedChange={setShopifySignInEnabled}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shopify-test-shops"><span key={tUi("testStores")}>{tUi("testStores")}</span></Label>
                  <Textarea
                    id="shopify-test-shops"
                    rows={3}
                    placeholder={"demo-shop.myshopify.com"}
                    value={shopifyTestShops}
                    onChange={(e) => setShopifyTestShops(e.target.value)}
                  />
                  <p className="text-xs text-gray-500"><TranslationText>{tUi.rich("oneTag0MyshopifyComTag0DomainPerLine", { tag0: chunks => (<code className="bg-gray-100 px-1 rounded">{chunks}</code>) })}</TranslationText></p>
                  {shopifyTestShopList.invalid.length > 0 && (
                    <p className="text-xs text-red-600 break-words">
                      {<span key={tUi("notAMyshopifyComDomainValue0", { value0: String(shopifyTestShopList.invalid.join(", ")) })}>{tUi("notAMyshopifyComDomainValue0", { value0: String(shopifyTestShopList.invalid.join(", ")) })}</span>}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      const saves: Array<() => Promise<unknown>> = [
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.clientId, shopifyClientId),
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.scopes, shopifyScopes),
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.handle, shopifyHandle),
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.signInEnabled, String(shopifySignInEnabled)),
                        () => updateSetting(SHOPIFY_APP_SETTING_KEYS.testShops, shopifyTestShopList.shops.join("\n")),
                      ]
                      // Only update secret if a new value was entered
                      if (shopifyClientSecret.trim()) {
                        saves.push(() => updateSetting(SHOPIFY_APP_SETTING_KEYS.clientSecret, shopifyClientSecret))
                      }
                      handleSave(saves)
                    }}
                    disabled={saveStatus === "saving" || shopifyTestShopList.invalid.length > 0}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      tUi("saving")
                    ) : (
                      <><TranslationText>{tUi.rich("tag0Tag0Save", { tag0: () => (<Save className="w-4 h-4 mr-2" />) })}</TranslationText></>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500">
                  <span><span key={tUi("callbackURL")}>{tUi("callbackURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/integrations/shopify/oauth/callback"}>{"/api/integrations/shopify/oauth/callback"}</span></span></code>
                </div>
                <p className="text-xs text-gray-500">
                  <span><span key={tUi("clientSecretIsStoredEncryptedAES256GCM")}>{tUi("clientSecretIsStoredEncryptedAES256GCM")}</span></span>{" "}
                  <a href="https://partners.shopify.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    <span key={tUi("shopifyPartnersDashboard")}>{tUi("shopifyPartnersDashboard")}</span></a>{" "}
                  <span><span key={tUi("withPublicDistributionLimitedVisibility")}>{tUi("withPublicDistributionLimitedVisibility")}</span></span>
                </p>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>


        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    <span><span key={tUi("metaFacebookMessengerWhatsApp")}>{tUi("metaFacebookMessengerWhatsApp")}</span></span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="meta-app-id"><span key={tUi("metaAppID")}>{tUi("metaAppID")}</span></Label>
                  <Input
                    id="meta-app-id"
                    type="text"
                    placeholder={tUi("yourMetaAppIDFromDeveloperPortal")}
                    value={metaAppId}
                    onChange={(e) => setMetaAppId(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-app-secret">
                    <span key={metaSecretConfigured ? tUi("updateAppSecret") : tUi("metaAppSecret")}>{metaSecretConfigured ? tUi("updateAppSecret") : tUi("metaAppSecret")}</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="meta-app-secret"
                      type={showMetaSecret ? "text" : "password"}
                      placeholder={metaSecretConfigured ? tUi("enterNewSecretToUpdate") : tUi("yourMetaAppSecret")}
                      value={metaAppSecret}
                      onChange={(e) => setMetaAppSecret(e.target.value)}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowMetaSecret((v) => !v)}
                    >
                      {showMetaSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {metaSecretConfigured && !metaAppSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      <span><span key={tUi("appSecretConfiguredEncrypted")}>{tUi("appSecretConfiguredEncrypted")}</span></span>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-webhook-verify-token"><span key={tUi("webhookVerifyToken")}>{tUi("webhookVerifyToken")}</span></Label>
                  <Input
                    id="meta-webhook-verify-token"
                    type="text"
                    placeholder={tUi("customStringForWebhookVerification")}
                    value={metaWebhookVerifyToken}
                    onChange={(e) => setMetaWebhookVerifyToken(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    <span key={tUi("sharedByTheFacebookWhatsAppAndInstagramWebhooks")}>{tUi("sharedByTheFacebookWhatsAppAndInstagramWebhooks")}</span></p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-sso-config-id"><span key={tUi("facebookAccountSSOConfigID")}>{tUi("facebookAccountSSOConfigID")}</span></Label>
                  <Input
                    id="meta-sso-config-id"
                    type="text"
                    placeholder={tUi("configurationIDForEmailAndPagesShowList")}
                    value={metaSsoConfigId}
                    onChange={(e) => setMetaSsoConfigId(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    <span key={tUi("dedicatedFacebookLoginForBusinessUserTokenConfiguration")}>{tUi("dedicatedFacebookLoginForBusinessUserTokenConfiguration")}</span></p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-fb-config-id"><span key={tUi("fBLoginForBusinessConfigID")}>{tUi("fBLoginForBusinessConfigID")}</span></Label>
                  <Input
                    id="meta-fb-config-id"
                    type="text"
                    placeholder={tUi("configurationIDFromFacebookLoginForBusiness")}
                    value={metaFbConfigId}
                    onChange={(e) => setMetaFbConfigId(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    <span key={tUi("createInMetaAppDashboardFacebookLoginFor")}>{tUi("createInMetaAppDashboardFacebookLoginFor")}</span></p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-whatsapp-config-id"><span key={tUi("whatsAppEmbeddedSignupConfigID")}>{tUi("whatsAppEmbeddedSignupConfigID")}</span></Label>
                  <Input
                    id="meta-whatsapp-config-id"
                    type="text"
                    placeholder={tUi("configurationIDFromWhatsAppEmbeddedSignup")}
                    value={metaWhatsappConfigId}
                    onChange={(e) => setMetaWhatsappConfigId(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    <span><span key={tUi("metaAppDashboardFacebookLoginForBusinessConfigurations")}>{tUi("metaAppDashboardFacebookLoginForBusinessConfigurations")}</span></span>
                    <strong> <span key={"WhatsApp Embedded Signup"}>{"WhatsApp Embedded Signup"}</span></strong><span><span key={tUi("powersTheConnectWhatsAppTileWhileEmptyThe")}>{tUi("powersTheConnectWhatsAppTileWhileEmptyThe")}</span></span>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="meta-lead-ads-config-id"><span key={tUi("metaLeadAdsConfigID")}>{tUi("metaLeadAdsConfigID")}</span></Label>
                  <Input
                    id="meta-lead-ads-config-id"
                    type="text"
                    placeholder={tUi("userAccessTokenConfigurationIDForLeadAds")}
                    value={metaLeadAdsConfigId}
                    onChange={(e) => setMetaLeadAdsConfigId(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500">
                    <span><span key={tUi("dedicatedFacebookLoginForBusinessConfigurationWithPage")}>{tUi("dedicatedFacebookLoginForBusinessConfigurationWithPage")}</span></span>
                    <code> pages_show_list</code><span>,</span> <code>pages_read_engagement</code><span>,</span>
                    <code> pages_manage_metadata</code><span><span key={tUi("and")}>{tUi("and")}</span></span> <code>leads_retrieval</code><span>.</span>
                  </p>
                </div>

                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                  <p className="text-xs font-medium text-gray-700">
                    <span key={tUi("requiredWebhookFieldsInTheMetaAppDashboard")}>{tUi("requiredWebhookFieldsInTheMetaAppDashboard")}</span></p>
                  <p className="text-xs text-gray-500">
                    <span key={tUi("subscribingTheCallbackURLIsNotEnoughEach")}>{tUi("subscribingTheCallbackURLIsNotEnoughEach")}</span></p>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>
                      <strong><span key={"Messenger"}>{"Messenger"}</span></strong> <span>→</span> <code>/api/webhooks/messaging/facebook</code><span>:</span>{" "}
                      <code>messages</code><span>,</span> <code>messaging_postbacks</code><span>,</span>{" "}
                      <code>message_reactions</code><span>,</span> <code>message_echoes</code><span><span key={tUi("metaAdsAlsoUsesThisPageCallbackWith")}>{tUi("metaAdsAlsoUsesThisPageCallbackWith")}</span></span> <code>leadgen</code><span>.</span>
                    </p>
                    <p>
                      <strong><span key={"WhatsApp"}>{"WhatsApp"}</span></strong> <span>→</span> <code>/api/webhooks/messaging/whatsapp</code><span>:</span>{" "}
                      <code>messages</code><span>,</span> <code>account_update</code><span>,</span>{" "}
                      <code>smb_message_echoes</code><span>,</span> <code>history</code><span>,</span>{" "}
                      <code>smb_app_state_sync</code>
                    </p>
                  </div>
                  <p className="text-xs text-gray-500">
                    <code>message_echoes</code> <span><span key={tUi("isWhatSurfacesOperatorRepliesSentFromThe")}>{tUi("isWhatSurfacesOperatorRepliesSentFromThe")}</span></span>
                  </p>
                  <p className="text-xs text-gray-500">
                    <span><span key={tUi("theLastThreeWhatsAppFieldsOnlyEverFire")}>{tUi("theLastThreeWhatsAppFieldsOnlyEverFire")}</span></span> <code>smb_message_echoes</code> <span><span key={tUi("carriesRepliesTheBusinessSentFromTheWhatsApp")}>{tUi("carriesRepliesTheBusinessSentFromTheWhatsApp")}</span></span> <code>account_update</code> <span><span key={tUi("isHowMetaReportsThatACustomerDisconnected")}>{tUi("isHowMetaReportsThatACustomerDisconnected")}</span></span>
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      const saves: Array<() => Promise<unknown>> = [
                        () => updateSetting("meta_app_id", metaAppId),
                        () => updateSetting("meta_webhook_verify_token", metaWebhookVerifyToken),
                        () => updateSetting("meta_sso_config_id", metaSsoConfigId),
                        () => updateSetting("meta_app_config_id", metaFbConfigId),
                        () => updateSetting("meta_lead_ads_config_id", metaLeadAdsConfigId),
                        () => updateSetting("meta_whatsapp_config_id", metaWhatsappConfigId),
                      ]
                      if (metaAppSecret.trim()) {
                        saves.push(() => updateSetting("meta_app_secret", metaAppSecret))
                      }
                      handleSave(saves)
                    }}
                    disabled={saveStatus === "saving"}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      tUi("saving")
                    ) : (
                      <><TranslationText>{tUi.rich("tag0Tag0Save", { tag0: () => (<Save className="w-4 h-4 mr-2" />) })}</TranslationText></>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    <span><span key={tUi("facebookCallbackURL")}>{tUi("facebookCallbackURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/agents/messaging/channels/facebook/callback"}>{"/api/agents/messaging/channels/facebook/callback"}</span></span></code>
                  </p>
                  <p>
                    <span><span key={tUi("facebookAccountSSORedirectURI")}>{tUi("facebookAccountSSORedirectURI")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/identity/callback/facebook"}>{"/api/identity/callback/facebook"}</span></span></code>
                  </p>
                  <p>
                    <span><span key={tUi("metaLeadAdsCallbackURL")}>{tUi("metaLeadAdsCallbackURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/integrations/meta-ads/oauth/callback"}>{"/api/integrations/meta-ads/oauth/callback"}</span></span></code>
                  </p>
                  <p>
                    <span><span key={tUi("facebookWebhookURL")}>{tUi("facebookWebhookURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/webhooks/messaging/facebook"}>{"/api/webhooks/messaging/facebook"}</span></span></code>
                  </p>
                  <p>
                    <span><span key={tUi("whatsAppWebhookURL")}>{tUi("whatsAppWebhookURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/webhooks/messaging/whatsapp"}>{"/api/webhooks/messaging/whatsapp"}</span></span></code>
                    {" "}<span><span key={tUi("setInWhatsAppConfigurationSubscribeThe")}>{tUi("setInWhatsAppConfigurationSubscribeThe")}</span></span> <code className="bg-gray-100 px-1 rounded">messages</code> <span><span key={tUi("fieldServesEveryConnectedNumberNoCallbackURL")}>{tUi("fieldServesEveryConnectedNumberNoCallbackURL")}</span></span>
                  </p>
                  <p>
                    <span><span key={tUi("appSecretIsStoredEncryptedAES256GCM")}>{tUi("appSecretIsStoredEncryptedAES256GCM")}</span></span>{" "}
                    <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      <span key={"Meta Developer Portal"}>{"Meta Developer Portal"}</span></a><span>.</span>
                  </p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Instagram className="w-5 h-5" />
                    <span><span key={"Instagram OAuth"}>{"Instagram OAuth"}</span></span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="instagram-app-id"><span key={tUi("instagramAppID")}>{tUi("instagramAppID")}</span></Label>
                  <Input
                    id="instagram-app-id"
                    type="text"
                    placeholder={tUi("appIDFromTheInstagramIGApp")}
                    value={instagramAppId}
                    onChange={(e) => setInstagramAppId(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instagram-app-secret">
                    <span key={instagramSecretConfigured ? tUi("updateAppSecret") : tUi("instagramAppSecret")}>{instagramSecretConfigured ? tUi("updateAppSecret") : tUi("instagramAppSecret")}</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="instagram-app-secret"
                      type={showInstagramSecret ? "text" : "password"}
                      placeholder={instagramSecretConfigured ? tUi("enterNewSecretToUpdate") : tUi("yourInstagramAppSecret")}
                      value={instagramAppSecret}
                      onChange={(e) => setInstagramAppSecret(e.target.value)}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowInstagramSecret((v) => !v)}
                    >
                      {showInstagramSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {instagramSecretConfigured && !instagramAppSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      <span><span key={tUi("appSecretConfiguredEncrypted")}>{tUi("appSecretConfiguredEncrypted")}</span></span>
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      const saves: Array<() => Promise<unknown>> = [
                        () => updateSetting("instagram_app_id", instagramAppId),
                      ]
                      if (instagramAppSecret.trim()) {
                        saves.push(() => updateSetting("instagram_app_secret", instagramAppSecret))
                      }
                      handleSave(saves)
                    }}
                    disabled={saveStatus === "saving"}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      tUi("saving")
                    ) : (
                      <><TranslationText>{tUi.rich("tag0Tag0Save", { tag0: () => (<Save className="w-4 h-4 mr-2" />) })}</TranslationText></>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    <span><span key={tUi("callbackURL")}>{tUi("callbackURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/agents/messaging/channels/instagram/callback"}>{"/api/agents/messaging/channels/instagram/callback"}</span></span></code>
                  </p>
                  <p>
                    <span><span key={tUi("webhookURL")}>{tUi("webhookURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/webhooks/messaging/instagram"}>{"/api/webhooks/messaging/instagram"}</span></span></code>
                  </p>
                  <p>
                    <span><span key={tUi("metaCreatesASeparateInstagramAppWith")}>{tUi("metaCreatesASeparateInstagramAppWith")}</span></span> <code className="bg-gray-100 px-1 rounded">-IG</code> <span><span key={tUi("suffixWhenYouConfigureAPISetupWithInstagram")}>{tUi("suffixWhenYouConfigureAPISetupWithInstagram")}</span></span>{" "}
                    <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      <span key={"Meta Developer Portal"}>{"Meta Developer Portal"}</span></a><span><span key={tUi("useTheAppIDAndSecretFromThat")}>{tUi("useTheAppIDAndSecretFromThat")}</span></span> <code className="bg-gray-100 px-1 rounded">-IG</code> <span><span key={tUi("appNotTheMainOneWebhookVerifyToken")}>{tUi("appNotTheMainOneWebhookVerifyToken")}</span></span>
                  </p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Music2 className="w-5 h-5" />
                    <span><span key={"TikTok Business Messaging"}>{"TikTok Business Messaging"}</span></span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="tiktok-app-id"><span key={tUi("tikTokAppID")}>{tUi("tikTokAppID")}</span></Label>
                  <Input
                    id="tiktok-app-id"
                    type="text"
                    placeholder={tUi("appIDFromMyAppsAppDetailBasic")}
                    value={tiktokAppId}
                    onChange={(e) => setTiktokAppId(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tiktok-app-secret">
                    <span key={tiktokSecretConfigured ? tUi("updateAppSecret") : tUi("tikTokAppSecret")}>{tiktokSecretConfigured ? tUi("updateAppSecret") : tUi("tikTokAppSecret")}</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="tiktok-app-secret"
                      type={showTiktokSecret ? "text" : "password"}
                      placeholder={tiktokSecretConfigured ? tUi("enterNewSecretToUpdate") : tUi("appSecretFromAppDetailBasicInformation")}
                      value={tiktokAppSecret}
                      onChange={(e) => setTiktokAppSecret(e.target.value)}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowTiktokSecret((v) => !v)}
                    >
                      {showTiktokSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {tiktokSecretConfigured && !tiktokAppSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      <span><span key={tUi("appSecretConfiguredEncrypted")}>{tUi("appSecretConfiguredEncrypted")}</span></span>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tiktok-authorization-url"><span key={tUi("accountHolderAuthorizationURL")}>{tUi("accountHolderAuthorizationURL")}</span></Label>
                  <Input
                    id="tiktok-authorization-url"
                    type="text"
                    placeholder={"https://www.tiktok.com/v2/auth/authorize?..."}
                    value={tiktokAuthorizationUrl}
                    onChange={(e) => setTiktokAuthorizationUrl(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      const saves: Array<() => Promise<unknown>> = [
                        () => updateSetting("tiktok_app_id", tiktokAppId),
                        () => updateSetting("tiktok_authorization_url", tiktokAuthorizationUrl),
                      ]
                      if (tiktokAppSecret.trim()) {
                        saves.push(() => updateSetting("tiktok_app_secret", tiktokAppSecret))
                      }
                      handleSave(saves)
                    }}
                    disabled={saveStatus === "saving"}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saveStatus === "saving" ? (
                      tUi("saving")
                    ) : (
                      <><TranslationText>{tUi.rich("tag0Tag0Save", { tag0: () => (<Save className="w-4 h-4 mr-2" />) })}</TranslationText></>
                    )}
                  </Button>
                </div>

                {/* The webhook subscription has no dashboard UI at TikTok — it
                    exists only as an API call, and the call needs the app secret,
                    so it runs server-side. One mutable callback URL per app: from
                    a non-production deployment this steals production's traffic. */}
                <div className="flex items-center justify-between gap-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs text-amber-900">
                    <div className="font-medium"><span key={tUi("webhookSubscription")}>{tUi("webhookSubscription")}</span></div>
                    <div>
                      <span key={tUi("pointsThisTikTokAppSDirectMessageWebhook")}>{tUi("pointsThisTikTokAppSDirectMessageWebhook")}</span></div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => updateTiktokWebhook.mutate()}
                    disabled={updateTiktokWebhook.isPending}
                  >
                    <span key={updateTiktokWebhook.isPending ? tUi("updating") : tUi("pointWebhookHere")}>{updateTiktokWebhook.isPending ? tUi("updating") : tUi("pointWebhookHere")}</span>
                  </Button>
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    <span><span key={tUi("redirectURL")}>{tUi("redirectURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/agents/messaging/channels/tiktok/callback"}>{"/api/agents/messaging/channels/tiktok/callback"}</span></span></code>
                  </p>
                  <p>
                    <span><span key={tUi("webhookURL")}>{tUi("webhookURL")}</span></span> <code className="bg-gray-100 px-1 rounded">{process.env.NEXT_PUBLIC_APP_URL || '[your-app-url]'}<span><span key={"/api/webhooks/messaging/tiktok"}>{"/api/webhooks/messaging/tiktok"}</span></span></code>
                  </p>
                  <p>
                    <span><span key={tUi("allThreeValuesComeFromMyAppsApp")}>{tUi("allThreeValuesComeFromMyAppsApp")}</span></span>{" "}
                    <a href="https://business-api.tiktok.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      <span key={tUi("tikTokForBusinessDeveloperPortal")}>{tUi("tikTokForBusinessDeveloperPortal")}</span></a>{" "}
                    <span><span key={tUi("notDevelopersTiktokComThatIsTheCreator")}>{tUi("notDevelopersTiktokComThatIsTheCreator")}</span></span>
                  </p>
                </div>

                {/* The app's scope selection is not visible anywhere at runtime, and a
                    missing one costs another review cycle to add. */}
                <div className="rounded-md border p-3 text-xs text-gray-600 space-y-2">
                  <div className="font-medium text-gray-900"><span key={tUi("appPermissionsScopeOfPermission")}>{tUi("appPermissionsScopeOfPermission")}</span></div>
                  <ul className="space-y-1">
                    <li>
                      <span className="font-medium"><span key={tUi("tikTokAccounts")}>{tUi("tikTokAccounts")}</span></span> <span><span key={tUi("authCodeManagementTokenExchangeAccountUserProfile")}>{tUi("authCodeManagementTokenExchangeAccountUserProfile")}</span></span>
                    </li>
                    <li>
                      <span className="font-medium"><span key={tUi("adAccountManagement")}>{tUi("adAccountManagement")}</span></span><span>,</span>{" "}
                      <span className="font-medium"><span key={tUi("cTXEventsManagement")}>{tUi("cTXEventsManagement")}</span></span><span>,</span>{" "}
                      <span className="font-medium"><span key={tUi("measurement")}>{tUi("measurement")}</span></span> <span><span key={tUi("unusedByUsButTikTokRequiresAllThree")}>{tUi("unusedByUsButTikTokRequiresAllThree")}</span></span>
                    </li>
                  </ul>
                  <p>
                    <span key={tUi("businessMessagingIsNotSelectableItIsGranted")}>{tUi("businessMessagingIsNotSelectableItIsGranted")}</span></p>
                  <p>
                    <span><span key={tUi("tikTokAccountsAlsoRequiresThe")}>{tUi("tikTokAccountsAlsoRequiresThe")}</span></span>{" "}
                    <a href="https://bytedance.sg.larkoffice.com/share/base/form/shrlgu4WEvtSXpEDLcCw56u4Rfc" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      <span key={tUi("accountsAPIAccessApplicationForm")}>{tUi("accountsAPIAccessApplicationForm")}</span></a>{" "}
                    <span className="font-medium"><span key={tUi("before")}>{tUi("before")}</span></span> <span><span key={tUi("theAppIsSubmittedSkippingItGetsBoth")}>{tUi("theAppIsSubmittedSkippingItGetsBoth")}</span></span>
                  </p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <Collapsible defaultOpen={false}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    <span><span key={tUi("voiceAIProviders")}>{tUi("voiceAIProviders")}</span></span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium"><span key={tUi("elevenLabsAccounts")}>{tUi("elevenLabsAccounts")}</span></h3>
                  <Button
                    size="sm"
                    onClick={() => { setElDialogAccount(null); setElDialogOpen(true) }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    <span><span key={tUi("addAccount")}>{tUi("addAccount")}</span></span>
                  </Button>
                </div>

                {isLoadingPool ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500"><span key={tUi("loadingAccounts")}>{tUi("loadingAccounts")}</span></span>
                  </div>
                ) : poolAccounts.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <span key={tUi("noElevenLabsAccountsConfiguredAddOneToGet")}>{tUi("noElevenLabsAccountsConfiguredAddOneToGet")}</span></div>
                ) : (
                  <div className="space-y-2">
                    {poolAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="border rounded-lg p-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{account.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {account.api_key_masked}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {account.status === "active" ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-destructive" />
                            )}
                            {account.webhook_id ? (
                              <span className="text-xs text-green-600"><span key={tUi("webhookOK")}>{tUi("webhookOK")}</span></span>
                            ) : (
                              <span className="text-xs text-yellow-600"><span key={tUi("noWebhook")}>{tUi("noWebhook")}</span></span>
                            )}
                            {account.is_default && (
                              <span className="text-xs bg-muted px-1.5 py-0.5 rounded"><span key={tUi("default")}>{tUi("default")}</span></span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setElDialogAccount(account)
                            setElDialogOpen(true)
                          }}
                        >
                          <span key={tUi("edit")}>{tUi("edit")}</span></Button>
                      </div>
                    ))}
                  </div>
                )}

                <ElevenLabsAccountDialog
                  open={elDialogOpen}
                  onOpenChange={setElDialogOpen}
                  account={elDialogAccount}
                  onSaved={loadPoolAccounts}
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    </div>
  )
}
